/**
 * OrbitsTab.jsx - Admin dashboard tab for orbit analytics
 * 
 * Shows:
 * - Overview stats (total orbits, ghost vs linked, users)
 * - Relationship type breakdown
 * - Invite tracking
 * - Recent activity
 * - Most active users
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
    Users, UserPlus, Link2, Ghost, Heart, MessageCircle, 
    TrendingUp, Activity, RefreshCw, Trash2, ChevronDown, ChevronUp
} from 'lucide-react';
import { BentoCard, StatDisplay, NeonIcon, NEON_COLORS, API_URL } from './shared';

// Relationship type colors
const RELATION_COLORS = {
    crush: '#FF69B4',
    partner: '#EC4899',
    friend: '#60A5FA',
    family: '#10B981',
    ex: '#9CA3AF',
    enemy: '#EF4444',
    other: '#6B7280'
};

const OrbitsTab = () => {
    const [stats, setStats] = useState(null);
    const [activity, setActivity] = useState([]);
    const [invites, setInvites] = useState([]);
    const [topConnections, setTopConnections] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedSection, setExpandedSection] = useState('overview');
    const [inviteFilter, setInviteFilter] = useState('all');
    
    // Create secret header for admin endpoints
    const getSecretHeader = () => ({
        'X-Internal-Secret': process.env.REACT_APP_INTERNAL_SECRET || ''
    });
    
    useEffect(() => {
        fetchAllData();
    }, []);
    
    const fetchAllData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const secretHeader = getSecretHeader();
            
            // Fetch all orbit admin data in parallel
            const [statsRes, activityRes, invitesRes, topRes] = await Promise.all([
                fetch(`${API_URL}/api/admin/orbits/stats`, { headers: secretHeader }),
                fetch(`${API_URL}/api/admin/orbits/activity?limit=20`, { headers: secretHeader }),
                fetch(`${API_URL}/api/admin/orbits/invites?status=${inviteFilter}&limit=50`, { headers: secretHeader }),
                fetch(`${API_URL}/api/admin/orbits/top-connections?limit=10`, { headers: secretHeader })
            ]);
            
            if (statsRes.ok) {
                setStats(await statsRes.json());
            }
            if (activityRes.ok) {
                const data = await activityRes.json();
                setActivity(data.activities || []);
            }
            if (invitesRes.ok) {
                const data = await invitesRes.json();
                setInvites(data.invites || []);
            }
            if (topRes.ok) {
                setTopConnections(await topRes.json());
            }
        } catch (err) {
            console.error('[ADMIN] Orbits fetch error:', err);
            setError(err.message);
        }
        
        setLoading(false);
    };
    
    const handleDeleteOrbit = async (orbitId) => {
        if (!confirm('Delete this orbit profile?')) return;
        
        try {
            const secretHeader = getSecretHeader();
            const res = await fetch(`${API_URL}/api/admin/orbits/${orbitId}`, {
                method: 'DELETE',
                headers: secretHeader
            });
            
            if (res.ok) {
                fetchAllData(); // Refresh
            }
        } catch (err) {
            console.error('[ADMIN] Delete orbit error:', err);
        }
    };
    
    const toggleSection = (section) => {
        setExpandedSection(expandedSection === section ? null : section);
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <motion.div
                    className="text-2xl"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                    ✧
                </motion.div>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="text-center py-12">
                <p className="text-red-400 mb-4">{error}</p>
                <button 
                    onClick={fetchAllData}
                    className="px-4 py-2 rounded-lg bg-primary/20 text-primary border border-primary/30"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-cinzel text-primary flex items-center gap-2">
                    <NeonIcon icon={Users} color={NEON_COLORS.purple} />
                    Orbits Analytics
                </h2>
                <button
                    onClick={fetchAllData}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                    <RefreshCw size={16} className="text-white/50" />
                </button>
            </div>

            {/* Overview Stats */}
            <BentoCard 
                className="cursor-pointer"
                onClick={() => toggleSection('overview')}
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <NeonIcon icon={Activity} color={NEON_COLORS.cyan} size={14} />
                        <span className="text-xs text-white/60 uppercase tracking-wider">Overview</span>
                    </div>
                    {expandedSection === 'overview' ? <ChevronUp size={14} className="text-white/40" /> : <ChevronDown size={14} className="text-white/40" />}
                </div>
                
                {expandedSection === 'overview' && stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <StatDisplay 
                            label="Total Orbits" 
                            value={Math.round(stats.overview?.total_orbits || 0)}
                            icon={Users}
                            neonColor="purple"
                        />
                        <StatDisplay 
                            label="Ghost Profiles" 
                            value={Math.round(stats.overview?.ghost_profiles || 0)}
                            icon={Ghost}
                            neonColor="cyan"
                        />
                        <StatDisplay 
                            label="Linked Profiles" 
                            value={Math.round(stats.overview?.linked_profiles || 0)}
                            icon={Link2}
                            neonColor="green"
                        />
                        <StatDisplay 
                            label="Users with Orbits" 
                            value={Math.round(stats.overview?.users_with_orbits || 0)}
                            icon={UserPlus}
                            neonColor="yellow"
                        />
                    </div>
                )}
                
                {expandedSection !== 'overview' && stats && (
                    <div className="flex items-center gap-4 text-sm text-white/60">
                        <span>{stats.overview?.total_orbits || 0} total</span>
                        <span>•</span>
                        <span>{stats.overview?.users_with_orbits || 0} users</span>
                        <span>•</span>
                        <span>{stats.overview?.avg_orbits_per_user || 0} avg/user</span>
                    </div>
                )}
            </BentoCard>

            {/* Relationship Types */}
            <BentoCard
                className="cursor-pointer"
                onClick={() => toggleSection('relations')}
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <NeonIcon icon={Heart} color={NEON_COLORS.pink} size={14} />
                        <span className="text-xs text-white/60 uppercase tracking-wider">Relationship Types</span>
                    </div>
                    {expandedSection === 'relations' ? <ChevronUp size={14} className="text-white/40" /> : <ChevronDown size={14} className="text-white/40" />}
                </div>
                
                {expandedSection === 'relations' && stats?.relationship_types && (
                    <div className="space-y-2">
                        {Object.entries(stats.relationship_types).map(([type, count]) => {
                            const color = RELATION_COLORS[type] || '#6B7280';
                            const total = stats.overview?.total_orbits || 1;
                            const percentage = Math.round((count / total) * 100);
                            
                            return (
                                <div key={type} className="flex items-center gap-3">
                                    <span className="w-16 text-xs capitalize" style={{ color }}>{type}</span>
                                    <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                                        <motion.div
                                            className="h-full rounded-full"
                                            style={{ background: color }}
                                            initial={{ width: 0 }}
                                            animate={{ width: `${percentage}%` }}
                                        />
                                    </div>
                                    <span className="w-12 text-xs text-white/50 text-right">{count}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {expandedSection !== 'relations' && stats?.relationship_types && (
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(stats.relationship_types).slice(0, 4).map(([type, count]) => (
                            <span 
                                key={type}
                                className="px-2 py-1 rounded-full text-[10px]"
                                style={{ 
                                    background: `${RELATION_COLORS[type]}20`,
                                    color: RELATION_COLORS[type]
                                }}
                            >
                                {type}: {count}
                            </span>
                        ))}
                    </div>
                )}
            </BentoCard>

            {/* Invites */}
            <BentoCard
                className="cursor-pointer"
                onClick={() => toggleSection('invites')}
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <NeonIcon icon={MessageCircle} color={NEON_COLORS.green} size={14} />
                        <span className="text-xs text-white/60 uppercase tracking-wider">Invites</span>
                    </div>
                    {expandedSection === 'invites' ? <ChevronUp size={14} className="text-white/40" /> : <ChevronDown size={14} className="text-white/40" />}
                </div>
                
                {expandedSection === 'invites' && (
                    <>
                        <div className="flex gap-4 mb-4">
                            <StatDisplay 
                                label="Total Generated" 
                                value={Math.round(stats?.invites?.total_generated || 0)}
                                neonColor="purple"
                            />
                            <StatDisplay 
                                label="Active" 
                                value={Math.round(stats?.invites?.currently_active || 0)}
                                neonColor="green"
                            />
                            <StatDisplay 
                                label="Acceptances" 
                                value={Math.round(stats?.invites?.total_acceptances || 0)}
                                neonColor="yellow"
                            />
                        </div>
                        
                        {/* Filter tabs */}
                        <div className="flex gap-2 mb-3">
                            {['all', 'active', 'expired', 'maxed'].map(f => (
                                <button
                                    key={f}
                                    onClick={(e) => { e.stopPropagation(); setInviteFilter(f); }}
                                    className={`px-2 py-1 rounded text-[10px] uppercase ${
                                        inviteFilter === f 
                                            ? 'bg-primary/20 text-primary' 
                                            : 'bg-white/5 text-white/50'
                                    }`}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>
                        
                        {/* Invite list */}
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {invites.slice(0, 10).map(inv => (
                                <div 
                                    key={inv.code}
                                    className="flex items-center justify-between p-2 rounded-lg bg-white/5"
                                >
                                    <div>
                                        <span className="text-xs font-mono text-white/70">{inv.code}</span>
                                        <span className="text-[10px] text-white/40 ml-2">by {inv.inviter_name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[10px] px-2 py-0.5 rounded ${
                                            inv.status === 'active' ? 'bg-green-500/20 text-green-400' :
                                            inv.status === 'expired' ? 'bg-red-500/20 text-red-400' :
                                            'bg-amber-500/20 text-amber-400'
                                        }`}>
                                            {inv.uses}/{inv.max_uses}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                
                {expandedSection !== 'invites' && stats?.invites && (
                    <div className="flex items-center gap-4 text-sm text-white/60">
                        <span>{stats.invites.total_generated || 0} generated</span>
                        <span>•</span>
                        <span>{stats.invites.currently_active || 0} active</span>
                        <span>•</span>
                        <span>{stats.invites.total_acceptances || 0} accepted</span>
                    </div>
                )}
            </BentoCard>

            {/* Recent Activity */}
            <BentoCard
                className="cursor-pointer"
                onClick={() => toggleSection('activity')}
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <NeonIcon icon={TrendingUp} color={NEON_COLORS.cyan} size={14} />
                        <span className="text-xs text-white/60 uppercase tracking-wider">Recent Activity</span>
                    </div>
                    {expandedSection === 'activity' ? <ChevronUp size={14} className="text-white/40" /> : <ChevronDown size={14} className="text-white/40" />}
                </div>
                
                {expandedSection === 'activity' && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {activity.map((act, i) => (
                            <div 
                                key={i}
                                className="flex items-start gap-3 p-2 rounded-lg bg-white/5"
                            >
                                <span className="text-lg">
                                    {act.type === 'orbit_created' ? '🔮' : '💌'}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs text-white/70">
                                        {act.type === 'orbit_created' ? (
                                            <>
                                                <span className="text-primary">{act.details?.name}</span>
                                                {' added as '}
                                                <span style={{ color: RELATION_COLORS[act.details?.relation_type] }}>
                                                    {act.details?.relation_type}
                                                </span>
                                            </>
                                        ) : (
                                            <>
                                                Invite <span className="font-mono text-primary">{act.details?.code}</span>
                                                {' created by '}{act.details?.inviter_name}
                                            </>
                                        )}
                                    </p>
                                    <p className="text-[10px] text-white/40">
                                        {new Date(act.timestamp).toLocaleString()}
                                    </p>
                                </div>
                                {act.type === 'orbit_created' && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDeleteOrbit(act.details?.orbit_id); }}
                                        className="p-1 rounded hover:bg-red-500/20 transition-colors"
                                    >
                                        <Trash2 size={12} className="text-red-400/50 hover:text-red-400" />
                                    </button>
                                )}
                            </div>
                        ))}
                        
                        {activity.length === 0 && (
                            <p className="text-sm text-white/40 text-center py-4">No recent activity</p>
                        )}
                    </div>
                )}
                
                {expandedSection !== 'activity' && (
                    <p className="text-sm text-white/60">
                        {stats?.last_7_days?.new_orbits || 0} new orbits this week
                    </p>
                )}
            </BentoCard>

            {/* Sun Sign Distribution */}
            {topConnections?.sun_sign_distribution && (
                <BentoCard>
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">☀️</span>
                        <span className="text-xs text-white/60 uppercase tracking-wider">Sun Sign Distribution</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {topConnections.sun_sign_distribution.map(({ sign, count }) => (
                            <span 
                                key={sign}
                                className="px-2 py-1 rounded-full text-[10px] bg-primary/10 text-primary/80"
                            >
                                {sign}: {count}
                            </span>
                        ))}
                    </div>
                </BentoCard>
            )}
        </div>
    );
};

export default OrbitsTab;
